import { MAX_FILE_SIZE_BYTES } from "./apiService"
import { decode, decodeImage, toRGBA8 } from "utif"
import { imageDimensionsFromData } from "image-dimensions"
import { heicTo } from "heic-to"

const MIN_PHOTO_DIMENSION = 132
const MIN_ASPECT_RATIO = 0.1 // 1:10
const MAX_ASPECT_RATIO = 10 // 10:1

export const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
]
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/tiff",
  "image/heic",
  "image/webp",
]
const ALLOWED_DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
]
const ALLOWED_IMAGE_EXTENSIONS = [
  ".jpeg",
  ".jpg",
  ".png",
  ".gif",
  ".tiff",
  ".heic",
  ".webp",
]

export const isTiff = (f: File) =>
  f.type === "image/tiff" || /\.tiff?$/i.test(f.name)

export const isHeic = (f: File) =>
  f.type === "image/heic" || f.type === "image/heif" || /\.heic?$/i.test(f.name)

async function tiffToPngBlob(file: File): Promise<Blob> {
  const buffer = await file.arrayBuffer()
  const ifds = decode(buffer)
  if (!ifds[0]) throw new Error("No IFD decoded from TIFF")
  decodeImage(buffer, ifds[0])
  if (!ifds[0].width || !ifds[0].height)
    throw new Error("TIFF has no valid dimensions")
  const rgba = toRGBA8(ifds[0])
  const canvas = document.createElement("canvas")
  canvas.width = ifds[0].width
  canvas.height = ifds[0].height
  const ctx = canvas.getContext("2d")!
  ctx.putImageData(
    new ImageData(
      new Uint8ClampedArray(rgba.buffer as ArrayBuffer),
      canvas.width,
      canvas.height,
    ),
    0,
    0,
  )
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("TIFF canvas render failed"))),
      "image/png",
    ),
  )
}

async function heicToPngBlob(file: File): Promise<Blob> {
  return heicTo({ blob: file, type: "image/png" })
}

/** Converts TIFF/HEIC to a PNG Blob so browsers can render them in <img>/<canvas>.
 *  Falls back to the raw file for formats browsers support natively. */
export async function toBrowserBlob(file: File): Promise<Blob> {
  try {
    if (isTiff(file)) return await tiffToPngBlob(file)
    if (isHeic(file)) return await heicToPngBlob(file)
  } catch {
    // Conversion failed; fall back to raw file — browser may still handle it.
  }
  return file
}

async function getHeicDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const uint8Array = new Uint8Array(arrayBuffer)
    const imageInfo = imageDimensionsFromData(uint8Array)
    if (imageInfo && imageInfo.width && imageInfo.height) {
      return { width: imageInfo.width, height: imageInfo.height }
    }
    throw new Error("imageDimensionsFromData did not return dimensions")
  } catch {
    throw new Error("Failed to decode HEIC dimensions")
  }
}

async function getTiffDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const arrayBuffer = await file.arrayBuffer()
  const ifds = decode(arrayBuffer)

  if (ifds && ifds[0]) {
    decodeImage(arrayBuffer, ifds[0])
    const { width, height } = ifds[0]
    if (width && height) {
      return { width, height }
    }
  }

  throw new Error("Failed to decode TIFF dimensions")
}

export async function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise(async (resolve, reject) => {
    try {
      if (isHeic(file)) {
        try {
          const dimensions = await getHeicDimensions(file)
          resolve(dimensions)
          return
        } catch (heicError) {
          console.log("HEIC conversion failed:", heicError)
          reject(
            new Error(
              `Failed to process HEIC image. Please try converting to JPEG or PNG format.`,
            ),
          )
          return
        }
      }

      if (isTiff(file)) {
        try {
          const dimensions = await getTiffDimensions(file)
          resolve(dimensions)
          return
        } catch (tiffError) {
          console.log("TIFF processing failed:", tiffError)
          reject(
            new Error(
              `Failed to process TIFF image: ${tiffError instanceof Error ? tiffError.message : "Unknown error"}`,
            ),
          )
          return
        }
      }

      // For other formats, use the traditional FileReader + Image approach
      const reader = new FileReader()

      reader.onload = () => {
        const img = new Image()
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }
        img.onerror = (_event, _source, _lineno, _colno, error) => {
          reject(new Error(`Failed to load image: ${error}`))
        }
        img.src = reader.result as string
      }

      reader.onerror = () => {
        reject(new Error("Failed to read file"))
      }

      reader.readAsDataURL(file)
    } catch (error) {
      reject(new Error(`Error processing image: ${error}`))
    }
  })
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".")
  return lastDotIndex !== -1
    ? fileName.substring(lastDotIndex).toLowerCase()
    : ""
}

export enum PhotoValidationOptions {
  IMAGE_TYPE = "imageType",
  NON_IMAGE_TYPE = "nonImageType",
  FILE_SIZE = "fileSize",
  IMAGE_VALIDATION = "imageValidation",
  NON_IMAGE_VALIDATION = "nonImageValidation",
}

/**
 * Validates a file (image or non-image) against specified criteria including type, size, and image-specific checks.
 * @param file - The file to be validated.
 * @param text - An object containing localized error messages for various validation failures.
 * @param exclusions - An array of PhotoValidationOptions to exclude specific validation checks.
 * @returns A promise that resolves to an object containing the upload status and an optional error message.
 */
export async function validateFile(
  file: File,
  text: {
    fileUploadError?: string
    errorFileType?: string
    errorFileSize?: string
    errorImageTooSmall?: string
    errorImageInvalidAspectRatio?: string
  },
  exclusions: PhotoValidationOptions[] = [],
): Promise<{ uploadStatus: UploadStatus; errorMessage?: string }> {
  const fileIsAnImage = ALLOWED_IMAGE_EXTENSIONS.includes(
    getFileExtension(file.name),
  )
  let fileTypesToCheck: string[] = []
  let fileExtensionsToCheck: string[] = []
  if (
    fileIsAnImage &&
    !exclusions.includes(PhotoValidationOptions.IMAGE_TYPE)
  ) {
    fileTypesToCheck = fileTypesToCheck.concat([...ALLOWED_IMAGE_TYPES])
    fileExtensionsToCheck = fileExtensionsToCheck.concat([
      ...ALLOWED_IMAGE_EXTENSIONS,
    ])
  } else if (
    !fileIsAnImage &&
    !exclusions.includes(PhotoValidationOptions.NON_IMAGE_TYPE)
  ) {
    fileTypesToCheck = fileTypesToCheck.concat([...ALLOWED_DOCUMENT_TYPES])
    fileExtensionsToCheck = fileExtensionsToCheck.concat([
      ...ALLOWED_DOCUMENT_EXTENSIONS,
    ])
  }

  // Check file type
  if (
    ((!!file.type && !fileTypesToCheck.includes(file.type)) ||
      !fileExtensionsToCheck.includes(getFileExtension(file.name))) &&
    ![
      PhotoValidationOptions.IMAGE_TYPE,
      PhotoValidationOptions.NON_IMAGE_TYPE,
    ].every((option) => exclusions.includes(option))
  ) {
    return { uploadStatus: "error", errorMessage: text?.errorFileType || "" }
  }

  // Check file size
  if (
    file.size > MAX_FILE_SIZE_BYTES &&
    !exclusions.includes(PhotoValidationOptions.FILE_SIZE)
  ) {
    return { uploadStatus: "error", errorMessage: text?.errorFileSize || "" }
  }

  // Check if image is too small and aspect ratio is valid
  // Note: These are both purely image related checks, so either is excluded
  if (
    fileIsAnImage &&
    !exclusions.includes(PhotoValidationOptions.IMAGE_VALIDATION)
  ) {
    // If getImageDimensions throws (e.g., broken/corrupt image), handle it
    // and return a controlled error response so callers don't receive an
    // uncaught promise rejection.
    let width: number, height: number
    try {
      const dims = await getImageDimensions(file)
      width = dims.width
      height = dims.height
    } catch {
      // Return a generic file upload error. Callers provide localized
      // messages via the `text` parameter; fall back to a sensible default.
      return {
        uploadStatus: "error",
        errorMessage: text?.fileUploadError || "",
      }
    }

    if (
      !!width &&
      !!height &&
      (width < MIN_PHOTO_DIMENSION || height < MIN_PHOTO_DIMENSION)
    ) {
      return {
        uploadStatus: "error",
        errorMessage: text?.errorImageTooSmall || "",
      }
    }

    const aspectRatio = width / height
    if (
      !!width &&
      !!height &&
      (aspectRatio < MIN_ASPECT_RATIO || aspectRatio > MAX_ASPECT_RATIO)
    ) {
      return {
        uploadStatus: "error",
        errorMessage: text?.errorImageInvalidAspectRatio || "",
      }
    }
  }

  return { uploadStatus: "success" }
}
